import moment from 'moment-timezone';

function sanitizeCsvField(value) {
    if (value == null) return '';
    let str = String(value);

    // Escape double quotes
    str = str.replace(/"/g, '""');

    // If contains comma, wrap in quotes
    if (str.includes(',')) {
        str = `"${str}"`;
    }

    // If starts with =, +, -, or @, escape it
    if (/^["]?([=+\-@])/.test(str)) {
        str = str.startsWith('"')
            ? `"\\t${str.slice(1)}"`
            : `\t${str}`;
    }

    return str;
}

export function flatten(obj, prefix = '', res = {}) {
    for (const key in obj) {
        const value = obj[key];
        const newKey = prefix ? `${prefix}.${key}` : key;
        if (Array.isArray(value)) {
            res[newKey] = JSON.stringify(value);
        } else if (value && typeof value === 'object') {
            flatten(value, newKey, res);
        } else {
            res[newKey] = value;
        }
    }
    return res;
}

/**
 * This function exports data to a CSV file.
 * It takes an array of objects and converts it to CSV format.
 * Then it creates a Blob from the CSV string and generates a download link.
 * Finally, it programmatically clicks the link to trigger the download.
 *
 * @param {Array} data - The data to be exported, should be an array of objects.
 * @param {string} fileName - The name of the file to be downloaded (without extension).
 * @returns {void}
 */
export const exportToCsv = (data, fileName) => {
    try {
        const csv = convertToCSV(data);
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${fileName}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } catch (error) {
        console.error(error?.message || 'Failed to export CSV');
    }
};

/**
 * Converts an array of objects to a CSV string.
 * Each object represents a row in the CSV.
 * The keys of the first object are used as headers.
 * Each value is enclosed in double quotes and escaped if necessary.
 *
 * @param {Array} data - The data to be converted, should be an array of objects.
 * @returns {string} - The CSV string representation of the data.
 */
function convertToCSV(data) {
    if (!Array.isArray(data) || data.length === 0) return '';
    const headers = Object.keys(data[0]);
    const csvRows = [];

    csvRows.push(headers.join(','));

    for (const row of data) {
        const values = headers.map(header => sanitizeCsvField(row[header]));
        csvRows.push(values.join(','));
    }

    return csvRows.join('\n');
}

/**
 * This function exports data to a CSV file but with specified headers and display names.
 * It takes an array of objects and converts it to CSV format.
 * Then it creates a Blob from the CSV string and generates a download link.
 * Finally, it programmatically clicks the link to trigger the download.
 *
 * @param {Array} data - The data to be exported, should be an array of objects.
 * @param {string} fileName - The name of the file to be downloaded (without extension).
 * @returns {void}
 */
export const exportToCsvWithHeaders = (data, fileName, headers, displayNames) => {
    try {
        const csv = convertToCSVWithHeaders(data, headers, displayNames);
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${fileName}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } catch (error) {
        console.error(error?.message || 'Failed to export CSV');
    }
};

/**
 * Converts an array of objects to a CSV string using provided headers and display names.
 * @param {Array} data - The data to be converted, should be an array of objects.
 * @param {Array} headers - The list of keys to include in the CSV, in order.
 * @param {Array} displayNames - The list of display names for the headers, in order.
 * @returns {string} - The CSV string representation of the data.
 */
export const convertToCSVWithHeaders = (data, headers, displayNames) => {
    if (!Array.isArray(data) || data.length === 0) return '';
    if (!Array.isArray(headers) || !Array.isArray(displayNames) || headers.length !== displayNames.length) {
        throw new Error('Headers and displayNames must be arrays of the same length.');
    }

    const csvRows = [];
    // Add the display names as the first row
    csvRows.push(displayNames.join(','));

    // Add the data rows
    for (const row of data) {
        const values = headers.map((header) => {
            let value = row[header];
            if (header === 'timestamp' && value) {
                value = moment(value).tz('UTC').format('MM-DD-YYYY, hh:mm:ss A');
            } else if (header === 'timestamp_pdt' && row['timestamp']) {
                value = moment(moment(row['timestamp']).tz('America/Los_Angeles')).format('MM-DD-YYYY, hh:mm:ss A');
            }
            return sanitizeCsvField(value);
        });
        csvRows.push(values.join(','));
    }
    return csvRows.join('\n');
};